import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import MouseRoundedIcon from "@mui/icons-material/MouseRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import { getDiagramAvailability, getProcessStructure } from "../../actions";
import { quickCheckAccess } from "../../utils/accessMapFunctions";
import { LOCKED_LABEL } from "../../features";
import { useViewConfig } from "../app/ViewConfigContext";
import {
  attributeLabel,
  describeCounts,
  displayValue,
  fieldsFor,
  formatLabel,
  tint,
  typeColour,
  typeInfo,
  typeLabel,
} from "../../utils/viewConfig";

// A structure map: the object's parts as cards in sequence order.
// Cards that have their own diagram or map can be opened.
// Three keeps every row close to the same height: a card with a long list no
// longer sets the height for the sparse ones beside it. The rest are one
// click away.
const CHILD_PREVIEW = 3;

// Parts arrive grouped by the relationship they were reached through, in the
// order the configuration puts those relationships in, each carrying the
// admin's own label for it and which way it points. One group - or parts with
// no relationship of their own, as an older server answers - reads as the
// flat list it always was; several get a quiet heading each, so a map that
// mixes "Contains" with "Documented by" says which is which.
const sectionsOf = (items) => {
  const sections = [];
  (items || []).forEach((item, index) => {
    const relationship = item?._relationship || "";
    const direction = item?._direction || "";
    const last = sections[sections.length - 1];
    if (last && last.relationship === relationship && last.direction === direction) last.entries.push({ item, index });
    else
      sections.push({
        relationship,
        direction,
        label: item?._relationshipLabel || "",
        entries: [{ item, index }],
      });
  });
  return sections;
};

// Whether a part opens: it has a diagram, or parts of its own - read from
// what this map fetched for it, else from the server, which computes
// hasAssignment from the graph (never a stored flag).
const hasParts = (item, children) =>
  (Array.isArray(children[item?.GUID]) && children[item.GUID].length > 0) || item?.hasAssignment === true;
const canOpen = (item, diagrams, children) => Boolean((diagrams[item?.GUID] || []).length) || hasParts(item, children);

const formatIcon = (format) =>
  format === "map" ? <GridViewRoundedIcon /> : format === "bpmn" ? <AccountTreeRoundedIcon /> : <ImageRoundedIcon />;

// The wash of a type's own colour a card is tinted with comes from
// `utils/viewConfig` (`tint`), so the map and the Flow wear the same colour
// for the same object type.

// The one line a card says about itself beneath its name: the first field the
// admin configured for cards of this type (Description, where nothing is
// configured), with its own name where that isn't obvious.
const subline = (config, item) => {
  const [first] = fieldsFor(config, "card", item);
  if (!first) return null;
  const value = displayValue(item[first]).trim();
  if (!value) return null;
  return { label: first === "Description" ? "" : attributeLabel(first), value };
};

export default function StructureCanvas({
  items,
  groups,
  total,
  truncated,
  // True where the server derived this order from the object's own stored
  // flow (backend/services/flowOrder.js). The map says so in one line: a
  // customer who reads the flow and the map side by side should be told why
  // they agree, not left to trust it.
  flowOrdered,
  fromGuid,
  subscription,
  selectedGuid,
  onSelect,
  onOpen,
}) {
  const config = useViewConfig();
  const [diagrams, setDiagrams] = useState({});
  const [children, setChildren] = useState({});
  // The admin's word for a relationship, where an older server sent the parts
  // without one on each part.
  const labelOf = (section) =>
    section.label ||
    (groups || []).find((group) => group && group.relationship === section.relationship)?.label ||
    section.relationship;
  // Scrollbars are hidden across the app, so the map says for itself when
  // there is more above or below.
  const scroller = useRef(null);

  // Built on the fly: which parts have diagrams, and what each part contains.
  useEffect(() => {
    let active = true;
    setChildren({});
    Promise.all(
      items.map((item) =>
        // Fetched from this object, so a part never shows the object it sits
        // under as something it contains.
        getProcessStructure(item.GUID, fromGuid).then((res) => [
          item.GUID,
          Array.isArray(res?.content?.assignments) ? res.content.assignments : [],
        ])
      )
    ).then(async (entries) => {
      if (!active) return;
      const byParent = Object.fromEntries(entries);
      setChildren(byParent);
      const allGuids = [...items.map((i) => i.GUID), ...entries.flatMap(([, list]) => list.map((c) => c.GUID))];
      const result = await getDiagramAvailability(allGuids);
      if (active) setDiagrams(result);
    });
    return () => {
      active = false;
    };
  }, [items, fromGuid]);

  return (
    <Box
      ref={scroller}
      // Focusable so the keyboard scrolls it: arrows, Page Up and Page Down.
      tabIndex={0}
      className="bf-structure"
      sx={{ height: "100%", overflowY: "auto", p: { xs: 1.5, md: 2.5 }, pb: { xs: 3, md: 4 }, boxSizing: "border-box" }}
    >
      {(() => {
        const openable = items.filter(
          (item) => quickCheckAccess(item?.inTrial, subscription) && canOpen(item, diagrams, children)
        ).length;
        return (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{describeCounts(config, items)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Click any name to see its properties.
                {openable > 0 && " Double-click, or use Open, to go one level deeper."}
              </Typography>
              {flowOrdered && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontWeight: 600 }}>
                  In the order of the {formatLabel(config, "bpmn")}.
                </Typography>
              )}
            </Box>
            {openable > 0 && (
              <Stack direction="row" spacing={0.75} alignItems="center" className="bf-structure__legend">
                <MouseRoundedIcon sx={{ fontSize: 16 }} />
                <span>
                  {openable} of {items.length} open on double-click
                </span>
              </Stack>
            )}
          </Stack>
        );
      })()}
      {sectionsOf(items).map((section, sectionIndex, sections) => (
        <Box
          key={`${section.relationship}|${section.direction}|${sectionIndex}`}
          component="section"
          sx={{ mb: sections.length > 1 ? 2.5 : 0 }}
        >
          {/* One group needs no heading - it is the whole map, and saying so
              twice only adds noise. Several get a quiet line each: what the
              relationship is called here, which way it points, and how many. */}
          {sections.length > 1 && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="baseline"
              sx={{ mb: 1, pb: 0.5, borderBottom: "1px solid rgba(26,26,46,0.08)" }}
            >
              <Typography
                component="h3"
                sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
              >
                {labelOf(section)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {section.entries.length}
              </Typography>
              {section.direction === "in" && (
                <Typography variant="caption" color="text.disabled" title="These point at this object">
                  ←
                </Typography>
              )}
            </Stack>
          )}
          <Box
            component="ol"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              display: "grid",
              // One value, so the space between cards reads the same in both
              // directions; stretch keeps every card in a row the same height.
              gap: 2,
              alignItems: "stretch",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(288px, 100%), 1fr))",
            }}
          >
            {section.entries.map(({ item, index }) => {
              const accessible = quickCheckAccess(item?.inTrial, subscription);
              const opens = accessible && canOpen(item, diagrams, children);
              const selected = selectedGuid === item.GUID;
              const formats = diagrams[item.GUID] || [];
              const withParts = hasParts(item, children);
              const info = typeInfo(config, item.Type);
              const says = subline(config, item);
              const parts = children[item.GUID];
              // A part with no diagram of its own opens straight on its map.
              const open = () => onOpen(item.GUID, formats.length ? undefined : "structure");
              return (
                <Box
                  component="li"
                  key={item.GUID || index}
                  role="button"
                  tabIndex={0}
                  aria-label={`${item.name}${opens ? ", can be opened" : ""}`}
                  onClick={() => onSelect(item)}
                  onDoubleClick={() => opens && open()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") opens ? open() : onSelect(item);
                  }}
                  className={`bf-structure__card${selected ? " is-selected" : ""}${accessible ? "" : " is-locked"}${opens ? " is-openable" : " is-static"}`}
                  title={opens ? "Double-click to open" : undefined}
                  style={{
                    "--accent": info.colour,
                    "--accent-wash": tint(info.colour, 0.1),
                    "--accent-line": tint(info.colour, 0.28),
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Box className="bf-structure__step">{index + 1}</Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {item.Type && (
                        <Box className="bf-structure__type" sx={{ mr: opens ? 2.5 : 0 }}>
                          <Box component="span" className="bf-structure__type-dot" />
                          {typeLabel(config, item.Type)}
                        </Box>
                      )}
                      <Typography className="bf-structure__title" component="h3" title={item.name}>
                        {item.name}
                      </Typography>
                      {says && (
                        <Typography variant="body2" className="bf-structure__sub">
                          {says.label && <Box component="span" className="bf-structure__sub-label">{says.label}</Box>}
                          {says.value}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  {opens && (
                    <Box className="bf-structure__hint" aria-hidden="true">
                      <MouseRoundedIcon sx={{ fontSize: 13 }} /> Double-click to open
                    </Box>
                  )}
                  {/* Nothing sits beneath this one, and it says nothing about
                      itself either: say so, rather than leave the card half empty. */}
                  {Array.isArray(parts) && parts.length === 0 && !says && (
                    <Box className="bf-structure__blank">
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.disabled" }}>
                        <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />
                        <Typography variant="caption">Nothing linked yet</Typography>
                      </Stack>
                    </Box>
                  )}
                  {(parts || []).length > 0 && (
                    <Box className="bf-structure__group" onClick={(e) => e.stopPropagation()}>
                      {/* What this card holds, named the way the rest of the app
                          names it: the admin's labels, counted per type. */}
                      <Box className="bf-structure__group-label">{describeCounts(config, parts)}</Box>
                      <Box component="ul" className="bf-structure__children">
                        {parts.slice(0, CHILD_PREVIEW).map((child) => {
                          const childOpens = quickCheckAccess(child?.inTrial, subscription) && canOpen(child, diagrams, children);
                          const openChild = () => onOpen(child.GUID, (diagrams[child.GUID] || []).length ? undefined : "structure");
                          return (
                            <Box
                              component="li"
                              key={child.GUID}
                              className={`bf-structure__child${selectedGuid === child.GUID ? " is-selected" : ""}`}
                              role="button"
                              tabIndex={0}
                              title={childOpens ? "Click for properties, double-click to open" : "Click for properties"}
                              onClick={() => onSelect(child)}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                if (childOpens) openChild();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") childOpens ? openChild() : onSelect(child);
                              }}
                            >
                              <span className="bf-structure__child-dot" style={{ background: typeColour(config, child.Type) }} />
                              <span className="bf-structure__child-name">{child.name}</span>
                              {childOpens && <OpenInFullRoundedIcon className="bf-structure__child-open" />}
                            </Box>
                          );
                        })}
                        {parts.length > CHILD_PREVIEW && (
                          <Box
                            component="li"
                            className="bf-structure__child bf-structure__child--more"
                            role="button"
                            tabIndex={0}
                            onClick={() => opens && open()}
                          >
                            {parts.length - CHILD_PREVIEW} more in {item.name}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    className="bf-structure__footer"
                  >
                    <Stack direction="row" spacing={0.5} sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.5 }}>
                      {(formats.length ? formats : withParts ? ["map"] : []).map((f) => (
                        <Box key={f} className="bf-structure__format">
                          {formatIcon(f)}
                          {formatLabel(config, f === "map" ? "structure" : f)}
                        </Box>
                      ))}
                      {!formats.length && !withParts && (
                        <Box className="bf-structure__format bf-structure__format--none">No diagram</Box>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {!accessible && <LockRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} titleAccess={LOCKED_LABEL} />}
                      <Tooltip title="Properties">
                        <IconButton
                          size="small"
                          className="bf-icon-btn"
                          aria-label={`Properties of ${item.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(item);
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {opens && (
                        <Button
                          size="small"
                          variant="contained"
                          className="bf-structure__open"
                          aria-label={`Open ${item.name}`}
                          startIcon={<OpenInFullRoundedIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            open();
                          }}
                        >
                          Open
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
      {/* The server answers one page of parts. Where there were more, the map
          says so rather than quietly stopping. */}
      {truncated && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing the first {items.length} of {total || items.length}.
        </Typography>
      )}
    </Box>
  );
}
